// eslint-disable-next-line import/no-extraneous-dependencies
import GoogleMap from 'google-map-react';

import { forwardRef } from 'react';
import { LandingBlock, LandingBlockProps } from '../LandingBlock/LandingBlock';

import styles from './LandingMap.module.scss';
import { MapTag } from './MapTag/MapTag';
import { Heading } from '../../Heading/Heading';
import { Icon } from '../../Icon/Icon';
import { Button } from '../../Button/Button';
import { SocialButton } from '../../SocialButton/SocialButton';
import { SocialMedias } from '../../NavBar/NavBar';

interface LandingMapProps extends LandingBlockProps {
  title?: string;
  direction?: string;
  text?: string;
  googleLink?: string;
  social?: SocialMedias;
  lat: number;
  lng: number;
}

export const LandingMap = forwardRef<HTMLElement, LandingMapProps>(
  (
    {
      direction,
      title = 'Visit Us Title',
      text,
      googleLink,
      social = null,
      lat,
      lng,
      ...props
    },
    ref
  ) => {
    const businessHours = [
      {
        day: 'Mon-Fri',
        time: '9AM - 4PM',
      },
      {
        day: 'Sat',
        time: '9AM - 2PM',
      },
      {
        day: 'Sun',
        time: 'Closed',
      },
    ];

    const onGoogleMaps = () => {
      if (!googleLink) return;

      window.open(googleLink, '_blank', 'noopener,noreferrer');
    };

    const renderHours = () =>
      businessHours.map((hour) => (
        <li className={styles.hour} key={hour.day}>
          <span>{hour.day}</span>
          <strong>{hour.time}</strong>
        </li>
      ));

    const hasSocial = Boolean(social?.whatsapp || social?.facebook);

    return (
      <LandingBlock classname={styles.root} {...props} ref={ref}>
        <div className={styles.content}>
          <div className={styles.info}>
            <span className={styles.eyebrow}>Location</span>
            <Heading className={styles.title} headingLevel="h2">
              {title}
            </Heading>
            {text && <p className={styles.description}>{text}</p>}
            <div className={styles.cards}>
              <article className={styles.card}>
                <div className={styles['card-title']}>
                  <Icon type="clock" alt="Business hours" />
                  <h3>Business hours</h3>
                </div>
                <ul className={styles['list-hour']}>{renderHours()}</ul>
              </article>
              <article className={styles.card}>
                <div className={styles['card-title']}>
                  <Icon type="location" alt="Address" />
                  <h3>Address</h3>
                </div>
                <p className={styles.address}>{direction}</p>
                <Button
                  className={styles.action}
                  btnStyle="primary"
                  icon="location"
                  iconPlace="left"
                  text="Open in Google Maps"
                  onClick={onGoogleMaps}
                  isDisabled={!googleLink}
                />
              </article>
              {hasSocial && (
                <article className={styles.card}>
                  <div className={styles['card-title']}>
                    <Icon type="phone" alt="Contact" />
                    <h3>Quick contact</h3>
                  </div>
                  <p className={styles.address}>
                    Message us directly or follow clinic updates.
                  </p>
                  <div className={styles['social-links']}>
                    {social.whatsapp && (
                      <SocialButton
                        className={styles['social-button']}
                        socialType="whatsapp"
                        link={social.whatsapp.link}
                      />
                    )}
                    {social.facebook && (
                      <SocialButton
                        className={styles['social-button']}
                        socialType="facebook"
                        link={social.facebook.link}
                      />
                    )}
                  </div>
                </article>
              )}
            </div>
          </div>
          <div className={styles.direction} aria-label="Map location">
            <div className={styles.map}>
              <GoogleMap
                bootstrapURLKeys={{
                  key: 'AIzaSyCfWXoa4HM-ApkJN9Uz7drWLZ4JISwAaY4',
                  libraries: ['places'],
                }}
                defaultCenter={{ lat: lat - 0.0005, lng: lng + 0.0005 }}
                defaultZoom={17}
                yesIWantToUseGoogleMapApiInternals
                options={{
                  disableDoubleClickZoom: true,
                  panControl: false,
                  disableDefaultUI: true,
                  scrollwheel: false,
                }}
                draggable={false}
              >
                <MapTag
                  lat={lat + 0.0002}
                  lng={lng}
                  direction={direction || ''}
                  googleLink={googleLink}
                />
              </GoogleMap>
            </div>
          </div>
        </div>
      </LandingBlock>
    );
  }
);

LandingMap.displayName = 'LandingMap';
